import { useMemo, useState } from "react";

import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import { toast } from "@jti/core-components/components/Toaster";
import { translateBackendErrorCode } from "@jti/fe-common";
import { CasProfileApi } from "@jti/rest-api";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import DiscountBlockMessage from "./DiscountBlockMessage";

import "./DiscountBlock.scss";

type Content = {
  description?: string;
  title?: string;
  buttonLabel?: string;
  subHeadline?: string;
};

export type DiscountBlockProps = {
  welcomeTitle?: string;
  welcomeSubHeadline?: string;
  welcomeText?: string;
  successTitle?: string;
  successText?: string;
  successButtonLabel?: string;
  successButtonUrl?: string;
  requestLimitErrorPageTitle?: string;
  requestLimitErrorPageText?: string;
  requestLimitErrorPageButtonLabel?: string;
  requestLimitErrorPageButtonUrl?: string;
  buttonLabel?: string;
  imageFileReference?: string;
  imageSmallFileReference?: string;
  backgroundImageFileReference?: string;
  backgroundImageSmallFileReference?: string;
  imageAlt?: string;
  backgroundImageAlt?: string;
};

type BackendError = {
  statusCode?: number;
  error_code?: string;
  message?: string;
};

const isBackendError = (error: unknown): error is BackendError => {
  return typeof error === "object" && error !== null && ("statusCode" in error || "error_code" in error);
};

const TOO_MANY_REQUEST_STATUS_CODE = 429;

const DiscountBlock = ({
  welcomeTitle,
  welcomeSubHeadline,
  welcomeText,
  successTitle,
  successText,
  successButtonLabel,
  successButtonUrl,
  requestLimitErrorPageTitle,
  requestLimitErrorPageText,
  requestLimitErrorPageButtonLabel,
  requestLimitErrorPageButtonUrl,
  buttonLabel,
  imageFileReference,
  imageSmallFileReference,
  backgroundImageFileReference,
  backgroundImageSmallFileReference,
  imageAlt,
  backgroundImageAlt,
}: DiscountBlockProps) => {
  const [status, setStatus] = useState<"welcome" | "success" | "error">("welcome");
  const [pending, setPending] = useState(false);
  const { __, dictionary } = useTranslations();

  const contentData = useMemo<Content>(() => {
    const welcomeData = {
      description: welcomeText,
      title: welcomeTitle,
      buttonLabel,
      subHeadline: welcomeSubHeadline,
    };

    const successData = {
      title: successTitle,
      description: successText,
      buttonLabel: successButtonLabel,
    };

    const errorData = {
      title: requestLimitErrorPageTitle,
      description: requestLimitErrorPageText,
      buttonLabel: requestLimitErrorPageButtonLabel,
    };
    switch (status) {
      case "welcome":
        return welcomeData;
      case "success":
        return successData;
      case "error":
        return errorData;
      default:
        return welcomeData;
    }
  }, [
    status,
    welcomeText,
    welcomeTitle,
    welcomeSubHeadline,
    successTitle,
    successText,
    successButtonLabel,
    requestLimitErrorPageTitle,
    requestLimitErrorPageText,
    requestLimitErrorPageButtonLabel,
    buttonLabel,
  ]);

  const requestDiscount = async () => {
    setPending(true);
    try {
      await CasProfileApi.init().requestDiscount();
      setStatus("success");
    } catch (error) {
      setPending(false);
      if (isBackendError(error) && error?.statusCode === TOO_MANY_REQUEST_STATUS_CODE) {
        return handleError();
      }

      let translatedErrorMessage = "";

      if (isBackendError(error)) {
        translatedErrorMessage =
          translateBackendErrorCode(error.error_code, undefined, dictionary) || error?.message || "";
      }
      toast.error(translatedErrorMessage || __("discount_block.error.message"));
    }
    setPending(false);
  };

  const handleError = () => {
    setStatus("error");
  };

  const onClickErrorButton = () => {
    if (requestLimitErrorPageButtonUrl) {
      window.location.href = requestLimitErrorPageButtonUrl;
    }
  };

  const onClickSuccessButton = () => {
    if (successButtonUrl) {
      window.location.href = successButtonUrl;
    }
  };

  const onButtonClick = () => {
    if (status === "welcome") {
      requestDiscount();
    }
    if (status === "error") {
      onClickErrorButton();
    }
    if (status === "success") {
      onClickSuccessButton();
    }
  };

  return (
    <div className={"discount-block__container"}>
      <DiscountBlockMessage
        {...contentData}
        variant={status}
        pending={pending}
        onClick={onButtonClick}
        mobileBackgroundImage={backgroundImageSmallFileReference}
        backgroundImageAlt={backgroundImageAlt}
      />
      <div className="discount-block__image-wrapper">
        <PictureBehaviour
          className="discount-block__picture"
          sources={[
            { srcSet: imageSmallFileReference, media: "(max-width: 959px)" },
            { srcSet: imageFileReference, media: "(min-width: 960px)" },
          ]}
          imageProps={{
            className: "discount-block__image",
            src: imageFileReference,
            alt: imageAlt,
          }}
        />
      </div>
      <img className="discount-block__background-image" src={backgroundImageFileReference} alt={backgroundImageAlt} />
    </div>
  );
};

export default DiscountBlock;
